import { Col } from "antd";
import { useFormikContext } from "formik";
import { Select, Form } from "formik-antd";
import { useCallback } from "react";
import { ReportColumn } from "/app/src/models";
import { useTranslation } from "react-i18next";

interface FormValues {
  groupColumnId: number | undefined;
  subGroupColumnId: number | undefined;
  finalGroupColumnId: number | undefined;
  groupRange?: "minute" | "hour" | "day" | "week" | "month" | "year";
}

/**
 * Renders form items for configuring grouping columns.
 * @param {Object} props - The component props.
 * @param {ReportColumn[]} props.columns - The columns available for grouping.
 * @returns {JSX.Element} The rendered grouping column form items.
 */
export default function GroupingColumnFormItems({
  columns,
}: {
  columns: ReportColumn[];
}) {
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const { t } = useTranslation();
  const handleGroupChange = useCallback(
    (fieldKey: string, fieldsToReset: string[]) => {
      return (newValue) => {
        setFieldValue(fieldKey, newValue);
        if (newValue === undefined) {
          fieldsToReset.forEach((field) => {
            setFieldValue(field, undefined);
          });
        }
      };
    },
    [setFieldValue],
  );

  const anyGroupedDateTimeColumn = useCallback(
    (values: FormValues) =>
      [
        values.groupColumnId,
        values.subGroupColumnId,
        values.finalGroupColumnId,
      ].some((columnId) =>
        columns.some(
          (column) =>
            column.id === columnId && column.filterType === "DateTime",
        ),
      ),
    [columns],
  );

  return (
    <>
      <Col span={6}>
        <Form.Item name="groupColumnId" hasFeedback={false}>
          <Select
            size="large"
            name="groupColumnId"
            placeholder={t("translation:select_group_column")}
            allowClear
            onChange={handleGroupChange("groupColumnId", [
              "subGroupColumnId",
              "finalGroupColumnId",
            ])}
          >
            {columns
              .filter(
                (column) =>
                  column.id !== values.subGroupColumnId &&
                  column.id !== values.finalGroupColumnId &&
                  column.type !== "count",
              )
              .map((column) => (
                <Select.Option key={column.id} value={column.id}>
                  {column.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="subGroupColumnId" hasFeedback={false}>
          <Select
            size="large"
            name="subGroupColumnId"
            placeholder={t("translation:select_sub_group_column")}
            allowClear
            disabled={!values.groupColumnId}
            onChange={handleGroupChange("subGroupColumnId", [
              "finalGroupColumnId",
            ])}
          >
            {columns
              .filter(
                (column) =>
                  column.id !== values.groupColumnId &&
                  column.id !== values.finalGroupColumnId &&
                  column.type !== "count",
              )
              .map((column) => (
                <Select.Option key={column.id} value={column.id}>
                  {column.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="finalGroupColumnId" hasFeedback={false}>
          <Select
            disabled={!values.subGroupColumnId || !values.groupColumnId}
            size="large"
            name="finalGroupColumnId"
            placeholder={t("translation:select_final_group_column")}
            allowClear
          >
            {columns
              .filter(
                (column) =>
                  column.id !== values.groupColumnId &&
                  column.id !== values.subGroupColumnId &&
                  column.type !== "count",
              )
              .map((column) => (
                <Select.Option key={column.id} value={column.id}>
                  {column.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={3}>
        <Form.Item name="groupRange">
          {anyGroupedDateTimeColumn(values) && (
            <Select
              name="groupRange"
              size="large"
              placeholder={t("translation:grouping_range")}
            >
              <Select.Option key="1" value="minute">
                {t("translation:minute")}
              </Select.Option>
              <Select.Option key="2" value="hour">
                {t("translation:hour")}
              </Select.Option>
              <Select.Option key="3" value="day">
                {t("translation:day")}
              </Select.Option>
              <Select.Option key="4" value="week">
                {t("translation:week")}
              </Select.Option>
              <Select.Option key="5" value="month">
                {t("translation:month")}
              </Select.Option>
              <Select.Option key="6" value="year">
                {t("translation:year")}
              </Select.Option>
            </Select>
          )}
        </Form.Item>
      </Col>
    </>
  );
}
